import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTypedSelector } from "../../../../../../../../store/reducers/use-typed-selector";
import { getDefaultAgendaImage } from "../../../../../../../../store/utils/create-event";
import { updatePageModuleAndSave } from "../../../../../../../../store/actions/admin/create-event/session";
import { PageModule, Session, SessionPanelLayoutsTypes, SessionTypesEnum, TranslateString } from "../../../../../../../../types/working-model";
import { getTemplateClassName } from "../../../../../../../../utils/utils";
import SuggestedCard from "./suggested-card";
import classNames from "classnames";
import { ESort } from "../../../../../../../../connection/sessions-panel/types";
import WaitingIndicator from "../../../../../../../general-ui/waiting-indicator/waiting-indicator";
import StaggerChildren from "../../../../../../../general-ui/animated/stagger-children";
import PanelSearch from "../../components/panel-search";
import { usePageModule, usePageModuleGroup } from "../../hooks/panel.hooks";
import { showAlert } from "../../../../../../../general-ui/alert/alert-service";
import { useDispatch } from "react-redux";
import { useGetAdminUrl } from "../../../../../../../../utils/admin-routing-utils";
import { useHistory, useParams } from "react-router";
import { SessionPanelMap } from "../../session-panel-route-map";
import Icon, { COLORS, ICONS } from "../../../../../../../general-ui/icon";

const sortOptions = [
	{ label: 'Date', value: ESort.dateDesc },
	{ label: 'Date Reverse', value: ESort.date },
	{ label: 'Name A-Z', value: ESort.name },
	{ label: 'Name Z-A', value: ESort.nameDesc },
];

const filterOptions = [
	{ label: 'All', value: '' },
	{ label: 'Broadcast', value: SessionTypesEnum.broadcast },
	{ label: 'On Demand', value: SessionTypesEnum.onDemand },
	{ label: 'Fireside', value: SessionTypesEnum.fireside },
	{ label: 'Breakout Rooms', value: SessionTypesEnum.breakoutRooms },
];

type FilterOptions = SessionTypesEnum | '';

const translateStringToSearchString = (tString: TranslateString): string => {
	let sString = '';
	for (const key in tString) {
		if (key !== 'changed' && typeof tString[key] === 'string') {
			sString += (" " + tString[key]);
		}
	}
	return sString;
};

const sessionFilter = (sessions: Session[], filter: FilterOptions) => {
	if (!filter) {
		return sessions;
	} else {
		return [...sessions].filter(session => session.session_type === filter);
	}
};

const sessionSearch = (sessions: Session[], searchTerm: string) => {
	return [...sessions].filter(session =>
		translateStringToSearchString(session.title).toLowerCase().includes(searchTerm.toLowerCase())
	);
};

const sortSessions = (sort: ESort, sessions: Session[]) => {
	return [...sessions].sort((a: Session, b: Session) => {
		const aTimestamp = a.timestamp || a.session;
		const bTimestamp = b.timestamp || b.session;
		switch (sort) {
			case ESort.date: {
				return aTimestamp < bTimestamp ? -1 : 1;
			}
			case ESort.dateDesc: {
				return aTimestamp > bTimestamp ? -1 : 1;
			}
			case ESort.name: {
				return a.title.base.toLowerCase() < b.title.base.toLowerCase() ? -1 : 1;
			}
			case ESort.nameDesc: {
				return a.title.base.toLowerCase() > b.title.base.toLowerCase() ? -1 : 1;
			}
			default: {
				return aTimestamp < bTimestamp ? -1 : 1;
			}
		}
	});
};

type Props = {
	withinTab?: boolean;
}

const SelectedSuggested: React.FC<Props> = ({ withinTab }) => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const template = getTemplateClassName(workingEvent?.template.name);
	const [sortOrder, setSortOrder] = useState(ESort.dateDesc);
	const [searchTerm, setSearchTerm] = useState('');
	const eventLoadedRef = useRef(false);
	const [userFilter, setUserFilter] = useState<FilterOptions>('');
	const containerRef = useRef<HTMLDivElement | null>(null);
	const pageModule = usePageModule();
	const [filteredSessions, setFilteredSessions] = useState(pageModule?.modules as Session[] ?? []);
	const { customPath } = useParams<{ customPath?: string }>();
	const dispatch = useDispatch();
	const adminPath = useGetAdminUrl();
	const history = useHistory();

	useEffect(() => {
		if (workingEvent?.sessions && !eventLoadedRef.current) {
			eventLoadedRef.current = true;
		}
	}, [workingEvent?.sessions]);

	const handleSort = useCallback((value: string) => {
		if (value) {
			setSortOrder(value as ESort);
		} else {
			setSortOrder(ESort.dateDesc);
		}
	}, []);

	const handleSearch = (term: string) => {
		setSearchTerm(term);
	};

	const addSuggested = () => {
		if (!pageModule?.id) return;

		const panelKey = customPath
			? SessionPanelLayoutsTypes.AddCustomSuggested
			: SessionPanelLayoutsTypes.AddExtraSuggested;

		history.push(adminPath({
			path: SessionPanelMap[panelKey],
			page_module: pageModule.id,
			customPath: customPath
		}));
	};

	const handleCardAction = useCallback((actionType: 'edit' | 'delete', item: number) => {
		if (!pageModule) return;

		if (actionType === 'edit') {
			console.warn('You can not edit sessions from here...');
		} else {
			dispatch(updatePageModuleAndSave({
				...pageModule,
				content_modules: pageModule?.content_modules?.filter(module => module !== item)
			}));
		}
	}, [pageModule, dispatch]);

	useEffect(() => {
		if (pageModule?.content_modules && workingEvent?.sessions) {
			const sessions = workingEvent.sessions.filter(session => pageModule.content_modules?.includes(session.session));
			const filteredSessions = sessionFilter(sessions, userFilter);
			const searchedSessions = sessionSearch(filteredSessions, searchTerm);
			const sortedSessions = sortSessions(sortOrder, searchedSessions);
			setFilteredSessions(sortedSessions);
		}
	}, [searchTerm, sortOrder, userFilter, pageModule?.content_modules, workingEvent?.sessions]);

	return (
		<div className={classNames("session-panel suggested-list")} ref={containerRef}>
			<PanelSearch
				sortOptions={sortOptions}
				sortOrder={sortOrder}
				handleSort={handleSort}
				filterOptions={filterOptions}
				userFilter={userFilter}
				handleFilter={(filter) => setUserFilter(filter as FilterOptions)}
				handleSearch={handleSearch}
			/>

			{filteredSessions.length ? (
				<StaggerChildren
					className="session-panel-content"
					footer={(
						<button
							className="no-style add-button"
							onClick={addSuggested}
						>
							<span>+ Add</span>
						</button>
					)}
				>
					{filteredSessions.map((session: Session) => (
						<SuggestedCard
							key={session.uuid}
							id={session.session}
							timestamp={session.timestamp}
							title={session.title}
							image={session.image || getDefaultAgendaImage(template, session.session)}
							clickHandler={handleCardAction}
						/>
					))}
				</StaggerChildren>
			) : (
				<div className="session-panel-no-results">
					<section>No Sessions</section>
				</div>
			)}
		</div>
	);
};

export default SelectedSuggested;